import * as React from 'react';
import { t } from '@reos/i18n';
import {
  addOfficeDomain,
  fetchOfficeDomains,
  removeOfficeDomain,
  verifyOfficeDomain,
  type OfficeDomain,
  type OfficeDomainList,
} from '../lib/office-site-api.js';
import { DomainApiError } from '../lib/api-error.js';

const cs = { locale: 'cs' } as const;

/**
 * Krok DOM1 — Nastavení → Web kanceláře → VLASTNÍ DOMÉNA.
 *
 * Vlastní karta hned za „Web kanceláře": patří k témuž webu, ale je to jiná
 * věc k vyřízení (jeden záznam u registrátora) a kancelář ji otevírá jednou
 * za život webu. Karta sama nic nerozhoduje — návod pro DNS i stav domény
 * chodí ze serveru (`/v1/crm/office-domains`), takže se text nápovědy nemůže
 * rozejít s tím, kam doména skutečně má mířit.
 *
 * SROZUMITELNÉ CHYBY (zadání bod 2): server posílá DATOVÝ kód důvodu
 * (`no_records`, `points_elsewhere`, `txt_missing`, `cert_pending`), věta
 * vzniká tady z katalogu @reos/i18n — žádná hláska ze serveru.
 */

/** Datový kód od serveru → věta pro kancelář. Neznámý kód = obecná věta. */
function reasonText(lastError: string | null): string | null {
  if (lastError === null) return null;
  if (lastError === 'no_records') return t('webAdmin.settings.customDomainReasonNoRecords', cs);
  if (lastError === 'points_elsewhere') {
    return t('webAdmin.settings.customDomainReasonPointsElsewhere', cs);
  }
  if (lastError === 'txt_missing') return t('webAdmin.settings.customDomainReasonTxtMissing', cs);
  if (lastError === 'cert_pending') return t('webAdmin.settings.customDomainReasonCertPending', cs);
  return t('webAdmin.settings.customDomainReasonOther', cs);
}

function statusText(status: OfficeDomain['status']): string {
  return t(`webAdmin.settings.customDomainStatus${status}`, cs);
}

export function OfficeCustomDomainCard(): React.JSX.Element {
  const [list, setList] = React.useState<OfficeDomainList | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchOfficeDomains().then(
      (loadedList) => {
        setList(loadedList);
        setLoaded(true);
      },
      () => setLoaded(true),
    );
  }, []);

  function fail(cause: unknown): void {
    setError(
      t('webAdmin.settings.officeWebsiteError', {
        ...cs,
        values: { error: cause instanceof DomainApiError ? cause.message : String(cause) },
      }),
    );
  }

  function add(): void {
    if (input.trim() === '') return;
    setBusy(true);
    setError(null);
    void addOfficeDomain(input.trim())
      .then((created) => {
        setInput('');
        setList((current) =>
          current === null ? current : { ...current, items: [...current.items, created] },
        );
      })
      .catch(fail)
      .finally(() => setBusy(false));
  }

  function verify(id: string): void {
    setBusy(true);
    setError(null);
    void verifyOfficeDomain(id)
      .then((updated) => {
        setList((current) =>
          current === null
            ? current
            : {
                ...current,
                items: current.items.map((item) => (item.id === id ? updated : item)),
              },
        );
      })
      .catch(fail)
      .finally(() => setBusy(false));
  }

  function remove(id: string): void {
    setBusy(true);
    setError(null);
    void removeOfficeDomain(id)
      .then(() => {
        setList((current) =>
          current === null
            ? current
            : { ...current, items: current.items.filter((item) => item.id !== id) },
        );
      })
      .catch(fail)
      .finally(() => setBusy(false));
  }

  if (!loaded || list === null) return <></>;
  const instructions = list.instructions;
  const full = list.items.length >= list.maxDomains;

  return (
    <section className="rh-a2-card" data-testid="settings-custom-domain">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.settings.customDomainTitle', cs)}</h2>
      </div>
      <p className="rh-a2-card__hint">{t('webAdmin.settings.customDomainHint', cs)}</p>

      {list.items.length === 0 ? (
        <p className="rh-a2-meta" data-testid="settings-custom-domain-empty">
          {t('webAdmin.settings.customDomainNoneNote', cs)}
        </p>
      ) : (
        <ul className="rh-a2-list" data-testid="settings-custom-domain-list">
          {list.items.map((item) => (
            <li key={item.id} data-testid={`settings-custom-domain-item-${item.domain}`}>
              <strong>{item.domain}</strong>{' '}
              <span data-testid={`settings-custom-domain-status-${item.domain}`}>
                {statusText(item.status)}
              </span>
              {reasonText(item.lastError) === null ? null : (
                <p
                  className="rh-a2-meta"
                  data-testid={`settings-custom-domain-reason-${item.domain}`}
                >
                  {reasonText(item.lastError)}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8, margin: '6px 0 12px' }}>
                <button
                  type="button"
                  className="rh-a2-btn"
                  disabled={busy}
                  onClick={() => verify(item.id)}
                  data-testid={`settings-custom-domain-verify-${item.domain}`}
                >
                  {t('webAdmin.settings.customDomainVerifyButton', cs)}
                </button>
                <button
                  type="button"
                  className="rh-a2-btn"
                  disabled={busy}
                  onClick={() => remove(item.id)}
                  data-testid={`settings-custom-domain-remove-${item.domain}`}
                >
                  {t('webAdmin.settings.customDomainRemoveButton', cs)}
                </button>
              </div>
              {/*
                NÁVOD AŽ U DOMÉNY, ne obecně: TXT token je na domenu, takže
                kancelář musí vidět právě ten svůj. Dokud doména běží
                (`ACTIVE`), návod se nekreslí — není co nastavovat.
              */}
              {item.status === 'ACTIVE' ? null : (
                <div data-testid={`settings-custom-domain-steps-${item.domain}`}>
                  <p className="rh-a2-meta">
                    <strong>{t('webAdmin.settings.customDomainStepsTitle', cs)}</strong>
                  </p>
                  <ol className="rh-a2-meta">
                    <li>
                      {t('webAdmin.settings.customDomainStepCname', {
                        ...cs,
                        values: { target: instructions.targetHost },
                      })}
                    </li>
                    <li>
                      {t('webAdmin.settings.customDomainStepApex', {
                        ...cs,
                        values: { ipv4: instructions.ipv4, ipv6: instructions.ipv6 },
                      })}
                    </li>
                    <li>
                      {t('webAdmin.settings.customDomainStepTxt', {
                        ...cs,
                        values: { name: instructions.txtName, token: item.verifyToken },
                      })}
                    </li>
                  </ol>
                  <p className="rh-a2-meta">{t('webAdmin.settings.customDomainStepWait', cs)}</p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {full ? null : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="rh-a2-meta" htmlFor="settings-custom-domain-input">
            {t('webAdmin.settings.customDomainInputLabel', cs)}
          </label>
          <input
            id="settings-custom-domain-input"
            className="rh-a2-input"
            value={input}
            placeholder={t('webAdmin.settings.customDomainPlaceholder', cs)}
            onChange={(event) => setInput(event.target.value)}
            data-testid="settings-custom-domain-input"
          />
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--primary"
            disabled={busy}
            onClick={add}
            data-testid="settings-custom-domain-add"
          >
            {t('webAdmin.settings.customDomainAddButton', cs)}
          </button>
        </div>
      )}

      {error === null ? null : (
        <p
          role="alert"
          className="rh-a2-error"
          style={{ margin: '10px 0 0' }}
          data-testid="settings-custom-domain-error"
        >
          {error}
        </p>
      )}
    </section>
  );
}
